import { supabase } from './supabase';

export interface DamagePoint {
  id: string;
  x: number; // X coordinate on vehicle diagram
  y: number; // Y coordinate on vehicle diagram
  damageType: 'scratch' | 'dent' | 'chip' | 'crack' | 'rust' | 'missing' | 'other';
  severity: 'minor' | 'moderate' | 'severe';
  notes: string;
  createdAt: string;
}

export interface VehicleDamageReport {
  id: string;
  business_id: string;
  booking_id: string;
  created_by?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: string;
  vehicle_color?: string;
  license_plate?: string;
  damage_points: DamagePoint[];
  additional_notes?: string;
  created_at: string;
  updated_at: string;
}

// Get damage report by booking ID
export async function getDamageReportByBookingId(bookingId: string): Promise<VehicleDamageReport | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('vehicle_damage_reports')
      .select('*')
      .eq('booking_id', bookingId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - damage report doesn't exist
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching damage report:', error);
    throw error;
  }
}

// Create or update damage report
export async function saveDamageReport(reportData: Partial<VehicleDamageReport>): Promise<VehicleDamageReport> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('vehicle_damage_reports')
      .upsert({
        ...reportData,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'booking_id'
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error saving damage report:', error);
    throw error;
  }
}

// Delete damage report
export async function deleteDamageReport(reportId: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { error } = await supabase
      .from('vehicle_damage_reports')
      .delete()
      .eq('id', reportId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting damage report:', error);
    throw error;
  }
}

// Check if damage report exists for booking
export async function hasDamageReport(bookingId: string): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    const { data, error } = await supabase
      .from('vehicle_damage_reports')
      .select('id')
      .eq('booking_id', bookingId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking damage report existence:', error);
    return false;
  }
}

// Generate damage report summary for liability waiver
export function generateDamageReportSummary(damageReport: VehicleDamageReport): string {
  if (!damageReport.damage_points || damageReport.damage_points.length === 0) {
    return 'No pre-existing damage documented.';
  }

  let summary = `Pre-existing Vehicle Damage Report:\n\n`;
  
  if (damageReport.vehicle_make || damageReport.vehicle_model) {
    summary += `Vehicle: ${damageReport.vehicle_year || ''} ${damageReport.vehicle_make || ''} ${damageReport.vehicle_model || ''}\n`;
  }
  
  if (damageReport.vehicle_color) {
    summary += `Color: ${damageReport.vehicle_color}\n`;
  }
  
  if (damageReport.license_plate) {
    summary += `License Plate: ${damageReport.license_plate}\n`;
  }
  
  summary += `\nDocumented Damage:\n`;
  
  damageReport.damage_points.forEach((point, index) => {
    summary += `${index + 1}. ${point.damageType.charAt(0).toUpperCase() + point.damageType.slice(1)} (${point.severity})`;
    if (point.notes) {
      summary += ` - ${point.notes}`;
    }
    summary += '\n';
  });

  if (damageReport.additional_notes) {
    summary += `\nAdditional Notes: ${damageReport.additional_notes}\n`;
  }

  summary += `\nBy signing this waiver, you acknowledge that the above damage was present before service began and that our company is not responsible for these pre-existing conditions.`;

  return summary;
}

// Damage type options for UI
export const DAMAGE_TYPES = [
  { value: 'scratch', label: 'Scratch', color: '#ff6b6b' },
  { value: 'dent', label: 'Dent', color: '#4ecdc4' },
  { value: 'chip', label: 'Chip', color: '#45b7d1' },
  { value: 'crack', label: 'Crack', color: '#f9ca24' },
  { value: 'rust', label: 'Rust', color: '#ff7675' },
  { value: 'missing', label: 'Missing Part', color: '#6c5ce7' },
  { value: 'other', label: 'Other', color: '#a0a0a0' }
] as const;

// Severity options for UI
export const SEVERITY_LEVELS = [
  { value: 'minor', label: 'Minor', color: '#00b894' },
  { value: 'moderate', label: 'Moderate', color: '#fdcb6e' },
  { value: 'severe', label: 'Severe', color: '#e17055' }
] as const; 